import { AppFunctionComponent } from "../../../../../types"
import React, { MouseEvent, useEffect, useState } from "react"
import useTranslationHook from "../../../../../hooks/use-translation.hook"
import { Annotation } from "../../../general-styles.component"
import { PrimaryHeading } from "../../../heading.component"
import { SectionLayout } from "../../section.component"
import {
  dialColors,
  stripeColors,
  watchImages,
  recommendedWatches,
} from "./mudita-moment-module.constants"
import {
  Dials,
  DialsSwitch,
  ModuleDescription,
  MomentArticle,
  MomentColorRadio,
  MomentImage,
  MomentSection,
  Straps,
  StrapsSwitch,
  SwitchWrapper,
  WatchNameHeading,
} from "./mudita-moment-module.styled"

const WatchImageResolver: AppFunctionComponent<{ name: string }> = ({
  name,
}) => {
  return (
    <>
      {Object.entries(watchImages).map(([watchName, img]) => {
        return (
          <MomentImage
            key={watchName}
            src={img}
            alt={watchName}
            active={name == watchName}
          />
        )
      })}
    </>
  )
}

const MuditaMomentModule = () => {
  const [activeStripe, setActiveStripe] = useState("Natural Cork")
  const [activeDial, setActiveDial] = useState("White")
  const [displayDials, setDisplayDials] = useState(false)
  const [displayStraps, setDisplayStraps] = useState(false)
  const { translate, dynamicTranslate } = useTranslationHook()

  const dialsActive = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDisplayDials(!displayDials)
    setDisplayStraps(false)
  }

  const strapsActive = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDisplayStraps(!displayStraps)
    setDisplayDials(false)
  }

  const resolveWatchName = (dial: string, stripe: string) => `${dial} ${stripe}`
  const resolveActiveWatchName = (dial: string, stripe: string) =>
    `${dial} Dial with ${stripe} Strap`

  useEffect(() => {
    setDisplayDials(false)
    setDisplayStraps(false)
  }, [activeDial, activeStripe])

  const activeWatch = resolveWatchName(activeDial, activeStripe)
  const activeWatchName = resolveActiveWatchName(activeDial, activeStripe)
  const isNR = activeWatchName.includes("NR")
  const resolveRecommendedWatches = (
    activeWatchName: string,
    recommendedWatchesArray: string[]
  ): string => {
    return recommendedWatchesArray.includes(activeWatchName)
      ? translate("recommended-text")
      : isNR
      ? translate("custom-choice-nr-text")
      : translate("custom-choice-text")
  }

  const recommendedAnnotation = resolveRecommendedWatches(
    activeWatchName,
    recommendedWatches
  )

  return (
    <MomentSection
      id="configurator"
      layout={SectionLayout.ImageText}
      data-testid="moment-module"
    >
      <WatchImageResolver name={activeWatch} />
      <PrimaryHeading>{translate("products-find-match")}</PrimaryHeading>
      <ModuleDescription>
        {translate("products-find-match-description")}
      </ModuleDescription>
      <MomentArticle>
        <SwitchWrapper
          active={displayDials === false && displayStraps === false}
        >
          <DialsSwitch onClick={dialsActive}>
            {translate("products-dial-color")}
          </DialsSwitch>
          <StrapsSwitch onClick={strapsActive}>
            {translate("products-strap-color")}
          </StrapsSwitch>
        </SwitchWrapper>
        <Dials displayDials={displayDials}>
          <Annotation as="span">{translate("products-select-dial")}</Annotation>
          <MomentColorRadio
            namePrefix="dial"
            products={dialColors}
            active={activeDial}
            onChange={setActiveDial}
            inputSize={90}
            spaceBetween={15}
          />
        </Dials>
        <Straps displayStraps={displayStraps}>
          <Annotation as="span">
            {translate("products-select-strap")}
          </Annotation>
          <MomentColorRadio
            namePrefix="strap"
            products={stripeColors}
            active={activeStripe}
            onChange={setActiveStripe}
            inputSize={90}
            spaceBetween={15}
          />
        </Straps>
      </MomentArticle>
      <WatchNameHeading active={!displayDials && !displayStraps}>
        {dynamicTranslate(activeWatchName)}
        <br />
        <Annotation as={"span"}>{recommendedAnnotation}</Annotation>
      </WatchNameHeading>
    </MomentSection>
  )
}

export default MuditaMomentModule
